import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { fetchDailyBoard } from "../../../api/TimeTracking/timeTrackingApi";
import {
  ttFilterFieldSx,
  ttKpiChipSx,
  ttSectionTitleSx,
  ttTableContainerSx,
} from "./timeTrackingStyles";
import TabContentShell from "./TabContentShell";
import { employeeInitials } from "./vacationCalendarUtils";
import {
  HOUR_TICKS,
  barStyleFromSession,
  formatDateHeading,
  formatHourLabel,
  formatMinutes,
  isTodayDate,
  localDateString,
  nowLineStyle,
  scheduleOverlayStyle,
  shiftDate,
} from "./dailyBoardUtils";
import ClockAdjustDialog from "./ClockAdjustDialog";
import {
  MobileEmptyState,
  MobileKpiBlock,
  MobileRecordCard,
} from "./TimeTrackingMobileCards";

const LABEL_WIDTH = 240;
const HOURS_WIDTH = 148;
const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 40;
const BAR_HEIGHT = 26;

function SessionBar({ session, theme }) {
  const style = barStyleFromSession(session);
  const start = new Date(session.start).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const end = new Date(session.end).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const color = theme.palette.success.main;

  return (
    <Tooltip
      title={`${start} – ${end}${session.open ? " (en curso)" : ""}`}
      arrow
    >
      <Box
        sx={{
          position: "absolute",
          top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
          height: BAR_HEIGHT,
          left: style.left,
          width: style.width,
          minWidth: 4,
          borderRadius: 1.25,
          bgcolor: alpha(color, session.open ? 0.55 : 0.9),
          border: "1.5px solid",
          borderColor: alpha(color, 0.95),
          boxShadow: `0 2px 8px ${alpha(color, 0.28)}`,
          backgroundImage: session.open
            ? `repeating-linear-gradient(-45deg, transparent, transparent 5px, ${alpha("#fff", 0.12)} 5px, ${alpha("#fff", 0.12)} 10px)`
            : "none",
        }}
      />
    </Tooltip>
  );
}

function DailyBoardMobileCard({ emp, theme }) {
  const accent = emp.isClockedIn
    ? theme.palette.success.main
    : theme.palette.primary.main;
  const worked = formatMinutes(emp.workedMinutes);
  const expected = formatMinutes(emp.expectedMinutes || 480);
  const hasSessions = emp.sessions?.length > 0;

  return (
    <MobileRecordCard
      theme={theme}
      accent={accent}
      avatarName={emp.username}
      title={emp.username}
      subtitle={
        emp.isClockedIn
          ? "Fichado ahora"
          : hasSessions
            ? `${emp.sessions.length} tramo(s)`
            : "Sin fichajes"
      }
      statusLabel={emp.isClockedIn ? "En curso" : null}
      statusColor="success"
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75}>
          <MobileKpiBlock
            theme={theme}
            label="Trabajadas"
            value={worked}
            color={hasSessions ? theme.palette.success.main : theme.palette.text.disabled}
          />
          <MobileKpiBlock
            theme={theme}
            label="Teóricas"
            value={expected}
            color={theme.palette.info.main}
          />
        </Stack>
        {emp.sessions?.map((session, idx) => {
          const start = new Date(session.start).toLocaleTimeString("es-ES", {
            hour: "2-digit",
            minute: "2-digit",
          });
          const end = new Date(session.end).toLocaleTimeString("es-ES", {
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <Box
              key={`${emp.id}-${idx}`}
              sx={{
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.success.main, session.open ? 0.1 : 0.06),
                border: "1px solid",
                borderColor: alpha(theme.palette.success.main, 0.2),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {start} – {end}
                {session.open ? " · en curso" : ""}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </MobileRecordCard>
  );
}

const DailyBoardTab = ({
  selectedUserId,
  canRrhh,
  employees = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accent = theme.palette.primary.main;
  const [date, setDate] = useState(() => localDateString());
  const [search, setSearch] = useState("");
  const [showSchedule, setShowSchedule] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { date };
      if (selectedUserId) params.userId = selectedUserId;
      const result = await fetchDailyBoard(params);
      setData(result);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar la vista del día");
    } finally {
      setLoading(false);
    }
  }, [date, selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const list = data?.employees || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => e.username?.toLowerCase().includes(q));
  }, [data, search]);

  const clockedInCount = (data?.employees || []).filter((e) => e.isClockedIn).length;
  const scheduleStyle = scheduleOverlayStyle();
  const nowLeft = data ? nowLineStyle(data.now, data.date) : null;
  const defaultRecordedAt = `${date}T09:00:00`;
  const showSearch = (data?.employees?.length || 0) > 1;
  const stripeBg = alpha(accent, 0.04);

  return (
    <Box>
      <TabContentShell accentColor={accent}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.25}
            alignItems={{ lg: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.25}
              sx={{
                alignSelf: { xs: "stretch", lg: "flex-start" },
                p: 0.35,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.45),
                boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.04)}`,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setDate((d) => shiftDate(d, -1))}
                sx={{ borderRadius: 1.5 }}
              >
                <ChevronLeftOutlinedIcon fontSize="small" />
              </IconButton>
              <TextField
                type="date"
                size="small"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 138,
                  "& .MuiInputBase-input": {
                    py: 0.75,
                    px: 0.5,
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    textAlign: "center",
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => setDate((d) => shiftDate(d, 1))}
                sx={{ borderRadius: 1.5 }}
              >
                <ChevronRightOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
              justifyContent={{ xs: "flex-start", lg: "flex-end" }}
            >
              <Chip label={formatDateHeading(date)} size="small" sx={ttKpiChipSx} />
              {isTodayDate(date) ? (
                <Chip
                  label="Hoy"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={ttKpiChipSx}
                />
              ) : null}
              <Chip
                label={`${filtered.length} empleado(s)`}
                size="small"
                variant="outlined"
                sx={ttKpiChipSx}
              />
              {isTodayDate(date) ? (
                <Chip
                  label={`${clockedInCount} fichado(s)`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={ttKpiChipSx}
                />
              ) : null}
              <Tooltip title="Actualizar">
                <IconButton
                  size="small"
                  onClick={load}
                  disabled={loading}
                  sx={{
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.5),
                    borderRadius: 1.5,
                  }}
                >
                  <RefreshOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canRrhh ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddOutlinedIcon />}
                  onClick={() => setAdjustOpen(true)}
                  sx={{ fontWeight: 700, borderRadius: 1.5, px: 1.5 }}
                >
                  Asignar fichaje
                </Button>
              ) : null}
            </Stack>
          </Stack>

          {showSearch ? (
            <TextField
              size="small"
              placeholder="Buscar empleado…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                ...ttFilterFieldSx(theme),
                width: { xs: "100%", sm: "auto" },
                maxWidth: { xs: "100%", sm: 280 },
                "& .MuiInputBase-root": {
                  ...ttFilterFieldSx(theme)["& .MuiInputBase-root"],
                  height: 36,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ fontSize: 18, opacity: 0.55 }} />
                  </InputAdornment>
                ),
              }}
            />
          ) : null}
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {filtered.map((emp) => (
              <DailyBoardMobileCard key={emp.id} emp={emp} theme={theme} />
            ))}
            {!filtered.length ? (
              <MobileEmptyState>Sin empleados para mostrar</MobileEmptyState>
            ) : null}
          </Stack>
        ) : (
          <Box sx={{ ...ttTableContainerSx(theme), overflow: "hidden" }}>
            <Box
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                borderBottom: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.5),
                bgcolor: alpha(theme.palette.background.paper, 0.55),
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ ...ttSectionTitleSx, mb: 0.25 }}>
                  Timeline 24h
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Verde = fichado · azul = horario 9–18h
                </Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={showSchedule}
                    onChange={(e) => setShowSchedule(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Horario teórico
                  </Typography>
                }
              />
            </Box>

            <Box
              sx={{
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                maxHeight: { xs: "68vh", md: "72vh" },
              }}
            >
              <Box sx={{ minWidth: 980 }}>
                {/* Cabecera */}
                <Box
                  sx={{
                    display: "flex",
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                    bgcolor: alpha(theme.palette.background.paper, 0.98),
                    borderBottom: "2px solid",
                    borderColor: alpha(accent, 0.18),
                  }}
                >
                  <Box
                    sx={{
                      width: LABEL_WIDTH,
                      flexShrink: 0,
                      height: HEADER_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      position: "sticky",
                      left: 0,
                      zIndex: 5,
                      bgcolor: alpha(theme.palette.background.paper, 0.98),
                      borderRight: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.7),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Empleado
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: HOURS_WIDTH,
                      flexShrink: 0,
                      height: HEADER_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.7),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Horas
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, position: "relative", height: HEADER_HEIGHT }}>
                    {HOUR_TICKS.map((min) => (
                      <Box
                        key={min}
                        sx={{
                          position: "absolute",
                          left: `${(min / (24 * 60)) * 100}%`,
                          height: "100%",
                          borderLeft: "1px solid",
                          borderColor: alpha(theme.palette.divider, 0.4),
                          pl: 0.5,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.65rem", fontWeight: 700, color: "text.secondary" }}
                        >
                          {formatHourLabel(min)}
                        </Typography>
                      </Box>
                    ))}
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        right: 4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: "text.secondary",
                      }}
                    >
                      23:59
                    </Typography>
                  </Box>
                </Box>

                {/* Filas */}
                {filtered.map((emp, rowIdx) => {
                  const hasSessions = emp.sessions?.length > 0;
                  const worked = formatMinutes(emp.workedMinutes);
                  const expected = formatMinutes(emp.expectedMinutes || 480);
                  const isStripe = rowIdx % 2 === 1;
                  const rowBg = isStripe ? stripeBg : "transparent";
                  const stickyBg = isStripe
                    ? stripeBg
                    : alpha(theme.palette.background.paper, 0.98);

                  return (
                    <Box
                      key={emp.id}
                      sx={{
                        display: "flex",
                        minHeight: ROW_HEIGHT,
                        bgcolor: rowBg,
                        borderBottom: "1px solid",
                        borderColor: alpha(theme.palette.divider, 0.22),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          bgcolor: alpha(accent, 0.07),
                          "& .db-employee-cell": {
                            bgcolor: alpha(accent, 0.07),
                          },
                        },
                      }}
                    >
                      <Box
                        className="db-employee-cell"
                        sx={{
                          width: LABEL_WIDTH,
                          flexShrink: 0,
                          px: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: stickyBg,
                          transition: "background-color 0.15s ease",
                          borderRight: "1px solid",
                          borderColor: alpha(theme.palette.divider, 0.45),
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: "0.72rem",
                            color: emp.isClockedIn
                              ? theme.palette.success.dark
                              : theme.palette.primary.dark,
                            bgcolor: emp.isClockedIn
                              ? alpha(theme.palette.success.main, 0.16)
                              : alpha(accent, 0.12),
                            border: "2px solid",
                            borderColor: emp.isClockedIn
                              ? alpha(theme.palette.success.main, 0.45)
                              : alpha(accent, 0.25),
                          }}
                        >
                          {employeeInitials(emp.username)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 800 }}
                            noWrap
                            title={emp.username}
                          >
                            {emp.username}
                          </Typography>
                          {emp.isClockedIn ? (
                            <Typography
                              variant="caption"
                              sx={{ color: "success.main", fontWeight: 700 }}
                            >
                              Fichado ahora
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {hasSessions
                                ? `${emp.sessions.length} tramo(s)`
                                : "Sin fichajes"}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          width: HOURS_WIDTH,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1,
                          borderRight: "1px solid",
                          borderColor: alpha(theme.palette.divider, 0.7),
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, textAlign: "center", lineHeight: 1.3 }}
                        >
                          <Box
                            component="span"
                            sx={{ color: hasSessions ? "text.primary" : "text.disabled" }}
                          >
                            {worked}
                          </Box>
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            {" / "}
                            {expected}
                          </Box>
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1, position: "relative", minHeight: ROW_HEIGHT }}>
                        {HOUR_TICKS.map((min) => (
                          <Box
                            key={`${emp.id}-${min}`}
                            sx={{
                              position: "absolute",
                              left: `${(min / (24 * 60)) * 100}%`,
                              top: 0,
                              bottom: 0,
                              borderLeft: "1px dashed",
                              borderColor: alpha(theme.palette.divider, 0.35),
                              pointerEvents: "none",
                            }}
                          />
                        ))}

                        {showSchedule ? (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 6,
                              bottom: 6,
                              left: scheduleStyle.left,
                              width: scheduleStyle.width,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.info.main, 0.06),
                              border: "1px dashed",
                              borderColor: alpha(theme.palette.info.main, 0.35),
                              pointerEvents: "none",
                            }}
                          />
                        ) : null}

                        {nowLeft ? (
                          <Box
                            sx={{
                              position: "absolute",
                              left: nowLeft,
                              top: 0,
                              bottom: 0,
                              width: 2,
                              borderLeft: "2px dashed",
                              borderColor: alpha(theme.palette.text.primary, 0.35),
                              zIndex: 1,
                              pointerEvents: "none",
                            }}
                          />
                        ) : null}

                        {emp.sessions?.map((session, idx) => (
                          <SessionBar
                            key={`${emp.id}-${idx}`}
                            session={session}
                            theme={theme}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}

                {!filtered.length ? (
                  <Box sx={{ py: 5, textAlign: "center" }}>
                    <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                      Sin empleados para mostrar
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        )}
      </TabContentShell>

      <ClockAdjustDialog
        open={adjustOpen}
        mode="add"
        entry={null}
        defaultUserId={selectedUserId}
        defaultRecordedAt={defaultRecordedAt}
        employees={employees}
        onClose={() => setAdjustOpen(false)}
        onSaved={load}
      />
    </Box>
  );
};

export default DailyBoardTab;
